import {
  DialogCloseReason,
  DialogService,
  SuiDialogBase,
  injectDirtyCloseHandler,
} from '@sui/dialog';
import { Injector, computed, inject } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { UnsavedChangesDialogComponent } from 'src/app/shared/components/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { firstValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

// * Interface describing the required contract for form editor components.
interface IFormEditorComponent {
  formGroup: FormGroup;
  formConfig: any; // ! FormConfiguration - using 'any' to avoid circular dependency
  selectedTabKey: string;
  activeTabIndex: number;
  /** Returns the in-flight save when the editor saves through a mutation, nothing on the NgRx form path. */
  submit(): Promise<unknown> | void;
  validateAndHandleInvalidForm(): boolean;
}

/**
 * Base class for dialogs using GeneralFormEditorComponent with CDK portals.
 *
 * @template TEditor - Type of editor component (extends GeneralFormEditorComponent)
 * @template TData - Type of data passed TO the dialog (via config.data)
 * @template TResult - Type of data returned FROM the dialog (via close)
 */
export abstract class FormEditorDialogBase<
  TEditor extends IFormEditorComponent,
  TData = unknown,
  TResult = unknown,
> extends SuiDialogBase<TData, TResult> {
  protected abstract editorComponent: TEditor;

  protected dialogService = inject(DialogService);
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  private injector = inject(Injector);

  private _isFormValid = computed(() => false);
  private _isFormDirty = computed(() => false);

  get isFormValid(): boolean {
    return this.editorComponent?.formGroup?.valid ?? false;
  }

  get isFormDirty(): boolean {
    return this.editorComponent?.formGroup?.dirty ?? false;
  }

  // * Initialize form editor dirty-close protection.
  // * Call this in ngAfterViewInit() after:
  // * - ViewChild editorComponent is available
  // * - Portals are setup
  protected initializeFormEditorProtection(): void {
    const formStatus = toSignal(this.editorComponent.formGroup.statusChanges, {
      initialValue: this.editorComponent.formGroup.status,
      injector: this.injector,
    });

    const formValue = toSignal(this.editorComponent.formGroup.valueChanges, {
      initialValue: this.editorComponent.formGroup.value,
      injector: this.injector,
    });

    this._isFormValid = computed(() => {
      formStatus();
      return this.editorComponent.formGroup.valid;
    });

    this._isFormDirty = computed(() => {
      formValue();
      return this.editorComponent.formGroup.dirty;
    });

    this.dirtyHandler.protectClosingWhenDirty(this._isFormDirty, async () => {
      if (!this._isFormDirty()) {
        // * аllow close
        return true;
      }

      const dialogRef = this.dialogService.openSimpleDialog(
        UnsavedChangesDialogComponent,
        {
          data: {
            isValid: this._isFormValid,
          },
          closeOnClick: false,
        },
      );

      const confirmResult = await firstValueFrom(dialogRef.closed);

      if (confirmResult.reason === DialogCloseReason.SaveAndClose) {
        if (!this._isFormValid()) {
          this.handleValidationErrors();
          // ! don't close, validation failed
          return false;
        }

        const saved = this.performSave() ?? undefined;

        const formData = this.editorComponent.formGroup.value;

        this.dirtyHandler.closeImmediately({
          reason: DialogCloseReason.Save,
          data: { formData, saved } as TResult,
        });

        // ! already closed via closeImmediately
        return false;
      }

      if (confirmResult.reason === DialogCloseReason.DiscardChanges) {
        // * allow close without saving
        return true;
      }

      // * user chose "Cancel" / ESC / backdrop click
      return false; // ! don't close
    });
  }

  private handleValidationErrors(): void {
    this.editorComponent.validateAndHandleInvalidForm();
  }

  /**
   * Runs the editor's save and hands back its promise (mutation path) so the close
   * event can carry it as `saved` - the dialog closes optimistically, and the opener
   * refreshes its row only once that promise resolved (`editorSaved`).
   */
  protected performSave(): Promise<unknown> | void {
    return this.editorComponent.submit();
  }

  public save(): void {
    const formGroup = this.editorComponent?.formGroup;
    if (!formGroup) {
      console.error('Form group not available');
      return;
    }

    if (formGroup.invalid) {
      this.handleValidationErrors();
      return;
    }

    const saved = this.performSave() ?? undefined;

    const formData = formGroup.value;

    // * close data is { formData, saved } - see SavedCloseData in editor-close.utils.ts
    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: { formData, saved } as TResult,
    });
  }

  public async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}

// * Example usage:
/*
export class EditorRoleDialogComponent extends FormEditorDialogBase<
  EditorRoleComponent,
  DialogData,
  DialogOutput
> {
  @ViewChild(EditorRoleComponent)
  protected override editorComponent!: EditorRoleComponent;

  ngAfterViewInit() {
    // Setup portals
    this.tabsPortal = this.portalRegistry.get('general-form-editor-tabs');
    this.formPortal = this.portalRegistry.get('general-form-editor-form');

    // Wire up form editor protection (tab jumping enabled by default)
    this.initializeFormEditorProtection();

    // Or disable tab jumping if not needed:
    // this.initializeFormEditorProtection({ enableTabJumping: false });
  }
}
*/
